import { Box } from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';
import services from '../../../services';
import { ReaderContext } from '../../Reader/ReaderContext';
import { Instance } from 'pspdfkit';
import useScreenSize from '../../../utils/useScreenSize';
import { sleepUntil } from '../../../utils/sleep';
import { borderRadius, customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import { useTranslation } from 'react-i18next';
import useRouter from '../../../utils/useRouter';
import { usePageReadTracking } from '../../utils/usePageReadTracking';

const createLeftArrowSvg = () => {
  // Create the SVG element
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');

  // Set attributes for the SVG element
  svg.setAttribute('xmlns', svgNS);
  svg.setAttribute('width', '25');
  svg.setAttribute('height', '24');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');

  // Create the first <path> element
  const path1 = document.createElementNS(svgNS, 'path');
  path1.setAttribute(
    'd',
    `M10.07 18.8191C9.88 18.8191 9.69 18.7491 9.54 18.5991L3.47 12.5291C3.18 
    12.2391 3.18 11.7591 3.47 11.4691L9.54 5.39914C9.83 5.10914 10.31 5.10914 
    10.6 5.39914C10.89 5.68914 10.89 6.16914 10.6 6.45914L5.06 11.9991L10.6 
    17.5391C10.89 17.8291 10.89 18.3091 10.6 18.5991C10.46 18.7491 10.26 18.8191 
    10.07 18.8191Z`
  );
  path1.setAttribute('fill', 'currentColor');

  // Create the second <path> element
  const path2 = document.createElementNS(svgNS, 'path');
  path2.setAttribute(
    'd',
    `M21 12.75H4.16998C3.75998 12.75 3.41998 12.41 3.41998 12C3.41998 11.59 3.75998 
    11.25 4.16998 11.25H21C21.41 11.25 21.75 11.59 21.75 12C21.75 12.41 21.41 12.75 21 12.75Z`
  );
  path2.setAttribute('fill', 'currentColor');

  // Append the <path> elements to the SVG
  svg.appendChild(path1);
  svg.appendChild(path2);

  return svg;
};

const createRightArrowSvg = () => {
  // Create the SVG element
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');

  // Set attributes for the SVG element
  svg.setAttribute('xmlns', svgNS);
  svg.setAttribute('width', '25');
  svg.setAttribute('height', '24');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');

  // Create the first <path> element
  const path1 = document.createElementNS(svgNS, 'path');
  path1.setAttribute(
    'd', 
    `M14.93 18.8191C14.74 18.8191 14.55 18.7491 14.4 18.5991C14.11 18.3091 14.11 17.8291 
    14.4 17.5391L19.94 11.9991L14.4 6.45914C14.11 6.16914 14.11 5.68914 14.4 5.39914C14.69 
    5.10914 15.17 5.10914 15.46 5.39914L21.53 11.4691C21.82 11.7591 21.82 12.2391 21.53 
    12.5291L15.46 18.5991C15.31 18.7491 15.12 18.8191 14.93 18.8191Z`);
  path1.setAttribute('fill', 'currentColor');

  // Create the second <path> element
  const path2 = document.createElementNS(svgNS, 'path');
  path2.setAttribute(
    'd',
    `M20.83 12.75H4C3.59 12.75 3.25 12.41 3.25 12C3.25 11.59 3.59 11.25 4 
    11.25H20.83C21.24 11.25 21.58 11.59 21.58 12C21.58 12.41 21.24 12.75 20.83 12.75Z`);
  path2.setAttribute('fill', 'currentColor');

  // Append the <path> elements to the SVG
  svg.appendChild(path1);
  svg.appendChild(path2);

  return svg;
};

const leftArrowSvg = createLeftArrowSvg();
const rightArrowSvg = createRightArrowSvg();

const ReaderContent = () => {
  const pdfContainer = useRef<HTMLIFrameElement>(null);
  const [width, setWidth] = useState<number>(0);
  const { isLargeScreen, isMediumScreen, isSmallScreen } = useScreenSize();
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const getPdfAreaBaselineWidth = () => {
    if (isLargeScreen) {
      const paddings = parseInt(customSizes.readerPage.paddingX) * 2 + parseInt(customSizes.pdfArea.paddingX) * 2;
      const readerDetailsWidth = parseInt(customSizes.readerDetails.width);
      const readerPagePreviewList = parseInt(customSizes.pagePreviewList.width);
      const size = window.innerWidth - paddings - readerDetailsWidth - readerPagePreviewList;
      return size;
    } else {
      const paddings = parseInt(customSizes.readerPage.paddingX) * 2;
      return window.innerWidth - paddings;
    }
  };
  const pdfAreaBaselineWidth = getPdfAreaBaselineWidth();

  const {
    mindItemId,
    currentPage,
    initialPage,
    nextSibling,
    previousSibling,
    setCurrentPage,
    zoomProperties,
    pspdfkitInstance,
    setPspdfkitInstance
  } = useContext(ReaderContext);

  usePageReadTracking(mindItemId, currentPage, isLoaded);

  const { t } = useTranslation();
  const { router } = useRouter();

  const { zoomFactor, setBaselineZoomFactor, baselineZoomFactor } = zoomProperties;

  const zoomFactorDecimal = zoomFactor && zoomFactor / 100;
  
  useEffect(() => {
    if (zoomFactorDecimal !== undefined && pspdfkitInstance) {
      pspdfkitInstance.setViewState(state => {
        return state.set('zoom', baselineZoomFactor * zoomFactorDecimal);
      });
      setWidth(pdfAreaBaselineWidth * zoomFactorDecimal);
    }
  }, [zoomFactor]);

  const getNavigationButtons = () => {
    const setElementStyle = (element: HTMLElement, styleObject: { [key: string]: string }) => {
      for (const [key, value] of Object.entries(styleObject)) {
        (element.style as any)[key] = value;
      }
    };

    // Create the container div
    const containerDiv = document.createElement('div');
    containerDiv.className = 'reader-navigation-container';
    setElementStyle(containerDiv, {
      display: 'flex',
      'justify-content': 'center',
      'align-items': 'center',
      'margin-top': spaces.md,
      'padding-bottom':`${
        parseInt(customSizes.readerPage.paddingY) +
        parseInt(customSizes.floatingToolbar.height) + 
        parseInt(spaces.xxxl)
      }px`,
      'gap': spaces.xs,
    });

    const buttonStyle = {
      display: 'flex',
      'justify-content': 'center',
      'align-items': 'center',
      'padding-top': spaces.xs,
      'padding-bottom': spaces.xs,
      'padding-left': spaces.lg,
      'padding-right': spaces.lg,
      'flex-direction': 'row',
      'border-radius': borderRadius.sm,
      border: `1px solid ${palette.primary.outlinedBorder}`,
      'font-family': 'Poppins',
      'font-size': '16px',
      'font-style': 'normal',
      'font-weight': '500',
      'line-height': '26px',
      'letter-spacing': '0.46px',
      color: palette.primary.main,
      cursor: 'pointer',
      background: 'transparent',
      gap: spaces.xs,
    };

    if (previousSibling) {
      const button1 = document.createElement('button');
      button1.onclick = () => {
        router.push(`/dokument/${previousSibling?.id}`);
      };
      button1.className = 'reader-navigation-button';
      button1.appendChild(leftArrowSvg);
      button1.appendChild(
        document.createTextNode(`${isSmallScreen ? t('Reader.Previous') : t('Previous unit')}`)
      );
      setElementStyle(button1, buttonStyle);
      containerDiv.appendChild(button1);
    }

    if (nextSibling) {
      const button2 = document.createElement('button');
      button2.onclick = () => {
        router.push(`/dokument/${nextSibling?.id}`);
      };
      button2.className = 'reader-navigation-button';
      button2.appendChild(
        document.createTextNode(`${isSmallScreen ? t('Reader.Next') : t('Next unit')}`)
      );
      button2.appendChild(rightArrowSvg);
      setElementStyle(button2, buttonStyle);

      containerDiv.appendChild(button2);
    }

    document.body.appendChild(containerDiv);

    return containerDiv;
  };

  const renderNavigationButtons = (instance: Instance) => {
    if (instance && (nextSibling || previousSibling)) {
      const spread = instance.contentDocument.querySelector('.PSPDFKit-Spread:last-of-type');
      spread?.append(getNavigationButtons());
    }
  };

  const isReaderNavigationButtonsRendered = (instance: Instance) => {
    if (!instance) {
      return false;
    }
    const buttons = instance.contentDocument.querySelector('.reader-navigation-button');
    return buttons !== null;
  };

  const setUpNavigationButtons = (instance: Instance) => {
    const spread = instance.contentDocument.querySelector('.PSPDFKit-Spread:last-of-type');
    const isLastPageRendered = spread?.getAttribute('data-spread-index') === `${instance.totalPageCount - 1}`;
    if (isReaderNavigationButtonsRendered(instance)) {
      return;
    }
    if (isLastPageRendered) {
      renderNavigationButtons(instance);
    } else {
      const renderNavigationButtonsOnLastPageRender = (pageIndex: number) => {
        if (!isReaderNavigationButtonsRendered(instance) && pageIndex === instance.totalPageCount - 1) {
          renderNavigationButtons(instance);
        }
      };

      instance.addEventListener('viewState.currentPageIndex.change', renderNavigationButtonsOnLastPageRender);
    }
  };

  useEffect(() => {
    const _currentPage = pspdfkitInstance && pspdfkitInstance.viewState.currentPageIndex + 1;
    if (_currentPage !== initialPage) {
      pspdfkitInstance && 
      pspdfkitInstance.setViewState(viewState => viewState.set('currentPageIndex', initialPage - 1));
    }
    setCurrentPage(initialPage || 1);
  }, [initialPage, pspdfkitInstance]);

  const load = async () => {
    setIsLoaded(false);
    const { token } = await services.mindItem.getMindItemToken(mindItemId);
    setWidth(pdfAreaBaselineWidth);

    const configuration = {
      container: pdfContainer.current,
      documentId: mindItemId,
      authPayload: { jwt: token },
      instant: false,
      toolbarItems: [],
      initialViewState: new window.PSPDFKit.ViewState({
        showToolbar: false,
        enableAnnotationToolbar: false,
        currentPageIndex: initialPage ? initialPage - 1 : currentPage - 1,
        allowPrinting: false,
        viewportPadding: {
          horizontal: 0,
          vertical: 0,
        },
      }),
      styleSheets: ['/newPdfReader.pspdfkit.css']
    };

    const _instance: Instance = await window.PSPDFKit.load(configuration);
    _instance.setViewState(viewState => viewState.set('zoom', 'FIT_TO_WIDTH'));

    _instance.addEventListener('viewState.currentPageIndex.change', (newCurrentPageIndex: number) => {
      setCurrentPage(newCurrentPageIndex + 1);
    });

    setBaselineZoomFactor(_instance.currentZoomLevel);
    setPspdfkitInstance?.(_instance);

    // make sure the pdf is scrolled to the top
    const scrollDiv = _instance.contentDocument.querySelector('.PSPDFKit-Scroll');
    scrollDiv?.scroll(0, 0);

    setIsLoaded(true);
  };

  const unload = () => {
    if (pspdfkitInstance) {
      window.PSPDFKit.unload(pspdfkitInstance);
      setPspdfkitInstance?.(undefined);
    }
  };
    
  useEffect(() => {
    unload();
    sleepUntil(() => window.PSPDFKit !== undefined, 15000)
      .then(() => {
        load();
      })
      .catch(() => {
        unload();
      });

    return () => {
      unload();
    };
  }, [mindItemId, isLargeScreen, isMediumScreen, isSmallScreen]);

  useEffect(() => {
    if (pspdfkitInstance) {
      setUpNavigationButtons(pspdfkitInstance);
    }
  }, [nextSibling, previousSibling, pspdfkitInstance]);

  return <Box sx={{
    height: '100%',
    overflowX: 'scroll',
    ...(!isLargeScreen && { width: '100%' }),
  }}
  data-testid='reader-content-container'
  >
    <Box
      sx={{
        width: width,
        height: '100%',
      }}
      data-testid='pdfContainer'
      ref={pdfContainer}
    />
  </Box>;
};

export default ReaderContent;
